import { useEffect, useRef, useState } from 'react'

/** Парсит ввод в неотрицательное число; пусто/мусор → 0. */
function parseNonNegative(value: string): number {
  if (value.trim() === '') return 0
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

interface NumberFieldProps {
  id: string
  value: number
  onChange: (n: number) => void
  placeholder?: string
  suffix?: string
  inputMode?: 'decimal' | 'numeric'
  ['aria-describedby']?: string
}

/** Разрешённый ввод: неотрицательное десятичное, включая промежуточные «0.», «». */
const NUMERIC_RE = /^\d*\.?\d*$/

/**
 * Числовое поле с ЛОКАЛЬНЫМ строковым буфером. Контролируемый `type=number` ломает
 * ввод дробей двумя путями: связка `value={x===0?'':x}` стирает набранный «0», а
 * сам `type=number` санитизирует промежуточные «0.» в пустую `.value`. Поэтому —
 * `type=text` + `inputMode` (числовая клавиатура на мобиле) + буфер, хранящий ровно
 * то, что печатает пользователь; наружу отдаём распарсенное число. Внешняя смена
 * `value` (сброс фильтров) ре-синхронизирует буфер. Ввод фильтруется регуляркой —
 * только цифры и одна точка.
 */
export function NumberField({
  id,
  value,
  onChange,
  placeholder,
  suffix,
  inputMode = 'decimal',
  ['aria-describedby']: describedBy,
}: NumberFieldProps) {
  const [text, setText] = useState(() => (value === 0 ? '' : String(value)))
  const lastValue = useRef(value)

  // Ре-синхронизация при внешнем изменении value (сброс/программная установка).
  useEffect(() => {
    if (value !== lastValue.current) {
      setText(value === 0 ? '' : String(value))
      lastValue.current = value
    }
  }, [value])

  function handle(raw: string) {
    if (!NUMERIC_RE.test(raw)) return // отклоняем нечисловой ввод (буфер не меняем)
    setText(raw)
    const n = parseNonNegative(raw)
    lastValue.current = n
    onChange(n)
  }

  return (
    <div className="input">
      <input
        id={id}
        type="text"
        inputMode={inputMode}
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        onChange={(e) => handle(e.target.value)}
        aria-describedby={describedBy}
      />
      {suffix ? <span className="input__suffix">{suffix}</span> : null}
    </div>
  )
}
